import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { CognitoUser, AuthenticationDetails, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { userDataPool } from './UserPool';
import axios from 'axios';
import { message } from 'antd';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dbUser, setDbUser] = useState(null);
  const [empresaData, setEmpresaData] = useState(null);
  const [permisos, setPermisos] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // Con esto lo que se hace es que se verifica que el usuario que está usando el sistema tenga 
  // el permiso necesario para operarlo
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const errorData = error.response?.data?.error;

        // Si está en medio del registro, no fuerces el cierre por errores de perfil ausente
        if (window.location.pathname.includes('/registro')) {
          return Promise.reject(error);
        }

        // Cuenta desactivada temporalmente
        if (status === 403 && errorData === 'cuenta_inactiva') {
          message.error("Acceso denegado: Tu cuenta ha sido desactivada.");
          cerrarSesionForzada();
        }

        // Usuario fantasma (Borrado de la BD pero con token en el navegador)
        if (status === 404) {
          console.warn("Sesión fantasma detectada. El usuario ya no existe en la base de datos.");
          cerrarSesionForzada();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Función de apoyo para no repetir código
  const cerrarSesionForzada = () => {
    const user = userDataPool.getCurrentUser();
    if (user) user.signOut();
    setIsAuthenticated(false);
    setDbUser(null);
    localStorage.clear();
    sessionStorage.clear();

    setTimeout(() => {
      window.location.href = '/login';
    }, 500);
  };

  // --- 1. FUNCIONES BÁSICAS (Definidas primero) ---
  const getJwtToken = useCallback(() => {
    const user = userDataPool.getCurrentUser();
    if (!user) return Promise.resolve(null);
    return new Promise((resolve, reject) => {
      user.getSession((err, session) => {
        if (err) return reject(err);
        resolve(session.getIdToken().getJwtToken());
      });
    });
  }, []);

  const fetchFullProfile = useCallback(async () => {
    try {
      const token = await getJwtToken();
      if (!token) return;

      const { data } = await axios.get(`${API_BASE_URL}/v1-usuarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (data.status === 'inactive') {
        const user = userDataPool.getCurrentUser();
        if (user) user.signOut();

        setIsAuthenticated(false);
        setDbUser(null);
        localStorage.clear();

        message.error("Tu cuenta ha sido desactivada por el administrador.");

        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return;
      }

      setDbUser(data);
      setPermisos(data.permisos || []);
      setEmpresaData({
        nombre: data.empresa_nombre || 'Yugonub POS',
        plan: 'Premium'
      });
    } catch (error) {
      console.error("Error cargando perfil de PostgreSQL:", error);

      // Si está en la página de registro, permite que el flujo continúe pacíficamente hacia el Paso 3
      if (window.location.pathname.includes('/registro')) {
        return;
      }

      if (error.response?.status === 401 || error.response?.status === 403) {
        const user = userDataPool.getCurrentUser();
        if (user) user.signOut();
        setIsAuthenticated(false);
        setDbUser(null);
        localStorage.clear();
        window.location.href = '/login';
      }
    }
  }, [getJwtToken, API_BASE_URL]);

  // --- 2. EFECTO DE CARGA INICIAL (Después de las funciones) ---
  useEffect(() => {
    const user = userDataPool.getCurrentUser();
    if (user) {
      user.getSession(async (err, cognitoSession) => {
        if (!err && cognitoSession.isValid()) {
          setIsAuthenticated(true);
          await fetchFullProfile();
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [fetchFullProfile]);

  const login = (username, password) => {
    const authenticationDetails = new AuthenticationDetails({ Username: username, Password: password });
    const cognitoUser = new CognitoUser({ Username: username, Pool: userDataPool });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async (result) => {
          setIsAuthenticated(true);
          await fetchFullProfile();
          resolve({ status: 'SUCCESS' });
        },
        onFailure: reject,
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          resolve({
            status: 'NEW_PASSWORD_REQUIRED',
            cognitoUser: cognitoUser
          });
        }
      });
    });
  };

  const completeNewPassword = (cognitoUser, newPassword, requiredAttributes = {}) => {
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, requiredAttributes, {
        onSuccess: async (result) => {
          setIsAuthenticated(true);
          await fetchFullProfile();
          resolve({ status: 'SUCCESS' });
        },
        onFailure: reject
      });
    });
  };

  const updateUser = async (payload) => {
    try {
      const token = await getJwtToken();
      const { data } = await axios.patch(`${API_BASE_URL}/v1-usuarios`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDbUser(data);
      return data;
    } catch (error) {
      console.error("Error actualizando usuario:", error);
      throw error;
    }
  };

  const signUp = (email, password, attributes) => {
    return new Promise((resolve, reject) => {
      const attributeList = [];

      attributeList.push(new CognitoUserAttribute({ Name: 'email', Value: email }));

      if (attributes.nombre) {
        attributeList.push(new CognitoUserAttribute({ Name: 'name', Value: attributes.nombre }));
      }
      if (attributes.telefono) {
        attributeList.push(new CognitoUserAttribute({ Name: 'phone_number', Value: attributes.telefono }));
      }

      userDataPool.signUp(email, password, attributeList, null, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result.user);
      });
    });
  };

  // 1. Solicita el código de seguridad al correo
  const solicitarRecuperacion = (username) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: username, Pool: userDataPool });
      cognitoUser.forgotPassword({
        onSuccess: function (data) { resolve(data); },
        onFailure: function (err) { reject(err); },
        inputVerificationCode: function (data) { resolve(data); }
      });
    });
  };

  // 2. Confirma el cambio con el código y la clave nueva
  const confirmarNuevaClave = (username, code, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: username, Pool: userDataPool });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: function () { resolve(); },
        onFailure: function (err) { reject(err); }
      });
    });
  };

  const resendSignUpCode = (username) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: username, Pool: userDataPool });
      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  const confirmCorreo = (username, code) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: username, Pool: userDataPool });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(true);
      });
    });
  };

  const logout = () => {
    const user = userDataPool.getCurrentUser();
    if (user) user.signOut();
    setIsAuthenticated(false);
    setDbUser(null);
  };

  // --- 4. SESIÓN UNIFICADA ---
  const session = useMemo(() => {
    // Si no está autenticado o la DB aún no responde, session es null
    if (!isAuthenticated || !dbUser) return null;

    return {
      user: {
        id: dbUser.id,
        nombre: dbUser.nombre,
        apellidos: dbUser.apellidos,
        email: dbUser.email,
        telefono: dbUser.telefono,
        avatar: dbUser.avatar_url,
        direccion: dbUser.direccion,
        provincia: dbUser.provincia,
        ciudad: dbUser.ciudad,
        code_postal: dbUser.code_postal,
        pais: dbUser.pais,
        idioma: dbUser.idioma,
        zona_horaria: dbUser.zona_horaria
      },
      empresa: {
        id: dbUser.empresa_id,
        nombre: empresaData?.nombre || 'Mi Negocio',
        permitir_edicion_precios: dbUser.permitir_edicion_precios,
        plan_actual: dbUser.plan_actual
      },
      rol: {
        nombre: dbUser.rol_nombre || 'Sin Rol',
        esCEO: dbUser.rol_nombre === 'CEO'
      }
    };
  }, [isAuthenticated, dbUser, empresaData]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        session,
        permisos,
        loading,
        login,
        logout,
        updateUser,
        getJwtToken,
        fetchFullProfile,
        signUp,
        confirmCorreo,
        completeNewPassword,
        resendSignUpCode,
        solicitarRecuperacion,
        confirmarNuevaClave
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};